/* A provider's shipping template: what each shipping speed costs and how long
   it takes, per region of the provider's country, plus fees abroad. */

export const SHIPPING_METHODS = [
  { name: 'Free Shipping', isFree: true },
  { name: 'Standard', isFree: false },
  { name: 'Priority', isFree: false },
  { name: 'Expedited', isFree: false },
  { name: '2-Day', isFree: false },
] as const;

/* Every template keeps these two; the others can be dropped and added back. */
const FIXED_METHODS = ['Free Shipping', 'Standard'];

export const MARKETPLACE_SPEEDS = ['Standard', 'Priority', 'Expedited'];

export const FEE_UNITS = [
  { value: 'lbs', label: 'Lb' },
  { value: 'item', label: 'Item' },
];

/* --- Wire shapes ------------------------------------------------------- */

export type RegionTree = {
  regionCode: string;
  regionName: string;
  subRegions: {
    subRegionCode: string;
    subRegionName: string;
    states: { stateCode: string; stateName: string }[];
  }[];
}[];

type WireConfiguration = {
  _id?: string;
  free?: boolean;
  marketplaces?: string[];
  transit_time?: { min_days?: number; max_days?: number };
  ship_charge?: {
    per_order?: { value?: number };
    per_item?: { price?: { value?: number }; unit_of_measure?: string };
  };
  regions?: {
    region_code: string;
    subRegions: { subRegionCode: string; states: string[] }[];
  }[];
};

type WireSpeed = { _id?: string; marketplace_id?: string; speed?: string };
type WireInternational = {
  _id?: string;
  marketplace_region?: string;
  price?: number | string;
};

export type ShippingTemplate = {
  marketplace_region_id?: { _id?: string; currency_code?: string };
  shipping_methods?: {
    method_name: string;
    configuration?: WireConfiguration[];
  }[];
  default_marketplace_shipping_speed?: WireSpeed[];
  international_shipment?: WireInternational[];
};

/* --- Editable shapes --------------------------------------------------- */

/** Region code → sub-region code → state code → state name. */
export type RegionSelection = Record<
  string,
  Record<string, Record<string, string>>
>;

export type MethodRow = {
  id: string;
  free: boolean;
  marketplaces: string[];
  regions: RegionSelection;
  minDays: string;
  maxDays: string;
  perOrder: string;
  perItem: string;
  unit: string;
};

export type ShippingMethod = { name: string; rows: MethodRow[] };

export type SpeedRow = {
  id: string;
  _id?: string;
  marketplaceId: string;
  speed: string;
};

export type InternationalRow = {
  id: string;
  _id?: string;
  regionId: string;
  price: string;
};

export type TemplateDraft = {
  methods: ShippingMethod[];
  speeds: SpeedRow[];
  international: InternationalRow[];
};

let draftId = 0;
/** A key for a row the server has not seen yet. */
export const newRowId = () => `new-${++draftId}`;

const text = (value: number | string | undefined) =>
  value === undefined || value === null ? '' : String(value);

function toSelection(
  regions: WireConfiguration['regions'],
  tree: RegionTree,
): RegionSelection {
  const selection: RegionSelection = {};
  for (const region of regions ?? []) {
    const known = tree.find(entry => entry.regionCode === region.region_code);
    const subRegions: RegionSelection[string] = {};
    for (const subRegion of region.subRegions ?? []) {
      const knownSub = known?.subRegions.find(
        entry => entry.subRegionCode === subRegion.subRegionCode,
      );
      const states: Record<string, string> = {};
      for (const code of subRegion.states ?? []) {
        states[code] =
          knownSub?.states.find(state => state.stateCode === code)?.stateName ??
          code;
      }
      subRegions[subRegion.subRegionCode] = states;
    }
    selection[region.region_code] = subRegions;
  }
  return selection;
}

/** The saved template as rows to edit; without one, every method starts empty. */
export function toDraft(
  template: ShippingTemplate | undefined,
  tree: RegionTree,
): TemplateDraft {
  const saved = template?.shipping_methods ?? [];
  const methods: ShippingMethod[] =
    saved.length > 0
      ? saved.map(method => ({
          name: method.method_name,
          rows: (method.configuration ?? []).map(configuration => ({
            id: configuration._id ?? newRowId(),
            free: !!configuration.free,
            marketplaces: configuration.marketplaces ?? [],
            regions: toSelection(configuration.regions, tree),
            minDays: text(configuration.transit_time?.min_days),
            maxDays: text(configuration.transit_time?.max_days),
            perOrder: text(configuration.ship_charge?.per_order?.value),
            perItem: text(configuration.ship_charge?.per_item?.price?.value),
            unit: configuration.ship_charge?.per_item?.unit_of_measure ?? '',
          })),
        }))
      : SHIPPING_METHODS.map(method => ({ name: method.name, rows: [] }));

  return {
    methods,
    speeds: (template?.default_marketplace_shipping_speed ?? []).map(speed => ({
      id: speed._id ?? newRowId(),
      _id: speed._id,
      marketplaceId: speed.marketplace_id ?? '',
      speed: speed.speed ?? '',
    })),
    international: (template?.international_shipment ?? []).map(row => ({
      id: row._id ?? newRowId(),
      _id: row._id,
      regionId: row.marketplace_region ?? '',
      price: text(row.price),
    })),
  };
}

export function isFreeMethod(name: string) {
  return SHIPPING_METHODS.some(method => method.name === name && method.isFree);
}

export function isRemovableMethod(name: string) {
  return !FIXED_METHODS.includes(name);
}

/** Methods the template can take back, in the order the menu lists them. */
export function addableMethods(methods: ShippingMethod[]) {
  return SHIPPING_METHODS.filter(
    method =>
      isRemovableMethod(method.name) &&
      !methods.some(entry => entry.name === method.name),
  ).map(method => method.name);
}

export function newMethodRow(
  name: string,
  regions: RegionSelection,
  marketplaces: string[],
): MethodRow {
  return {
    id: newRowId(),
    free: isFreeMethod(name),
    marketplaces,
    regions,
    minDays: '',
    maxDays: '',
    perOrder: '0',
    perItem: '0',
    unit: '',
  };
}

/* --- Regions ----------------------------------------------------------- */

/** Every state the selections hold between them. */
export function mergeSelections(selections: RegionSelection[]) {
  const merged: RegionSelection = {};
  for (const selection of selections) {
    for (const [region, subRegions] of Object.entries(selection)) {
      merged[region] ??= {};
      for (const [subRegion, states] of Object.entries(subRegions)) {
        merged[region][subRegion] = { ...merged[region][subRegion], ...states };
      }
    }
  }
  return merged;
}

export type RegionState = {
  regionCode: string;
  subRegionCode: string;
  stateCode: string;
  stateName: string;
};

/** The states under a region or sub-region that no other row of the method holds. */
export function freeStates(
  tree: RegionTree,
  taken: RegionSelection,
  regionCode: string,
  subRegionCode?: string,
): RegionState[] {
  const region = tree.find(entry => entry.regionCode === regionCode);
  return (region?.subRegions ?? [])
    .filter(sub => !subRegionCode || sub.subRegionCode === subRegionCode)
    .flatMap(sub =>
      sub.states
        .filter(
          state => !taken[regionCode]?.[sub.subRegionCode]?.[state.stateCode],
        )
        .map(state => ({
          regionCode,
          subRegionCode: sub.subRegionCode,
          stateCode: state.stateCode,
          stateName: state.stateName,
        })),
    );
}

export function isTaken(taken: RegionSelection, state: RegionState) {
  return !!taken[state.regionCode]?.[state.subRegionCode]?.[state.stateCode];
}

export function isSelected(selection: RegionSelection, state: RegionState) {
  return !!selection[state.regionCode]?.[state.subRegionCode]?.[
    state.stateCode
  ];
}

/** Adds or drops the states; a sub-region or region left empty goes too. */
export function toggleStates(
  selection: RegionSelection,
  states: RegionState[],
  on: boolean,
): RegionSelection {
  const next: RegionSelection = {};
  for (const [region, subRegions] of Object.entries(selection)) {
    next[region] = {};
    for (const [subRegion, entries] of Object.entries(subRegions)) {
      next[region][subRegion] = { ...entries };
    }
  }
  for (const state of states) {
    if (on) {
      next[state.regionCode] ??= {};
      next[state.regionCode][state.subRegionCode] ??= {};
      next[state.regionCode][state.subRegionCode][state.stateCode] =
        state.stateName;
    } else {
      delete next[state.regionCode]?.[state.subRegionCode]?.[state.stateCode];
    }
  }
  for (const [region, subRegions] of Object.entries(next)) {
    for (const [subRegion, entries] of Object.entries(subRegions)) {
      if (Object.keys(entries).length === 0) delete subRegions[subRegion];
    }
    if (Object.keys(subRegions).length === 0) delete next[region];
  }
  return next;
}

/** Checked when every free state is picked, mixed when only some are. */
export function checkState(selection: RegionSelection, states: RegionState[]) {
  const picked = states.filter(state => isSelected(selection, state)).length;
  return {
    isSelected: states.length > 0 && picked === states.length,
    isIndeterminate: picked > 0 && picked < states.length,
  };
}

/** One line per region: its states, or "all states" when it is whole. */
export function describeRegions(selection: RegionSelection, tree: RegionTree) {
  return Object.entries(selection).map(([code, subRegions]) => {
    const region = tree.find(entry => entry.regionCode === code);
    const names = Object.values(subRegions).flatMap(states =>
      Object.values(states),
    );
    const total =
      region?.subRegions.reduce((sum, sub) => sum + sub.states.length, 0) ?? 0;
    const name = region?.regionName ?? code;
    return total > 0 && names.length === total
      ? `${name}: all states`
      : `${name}: ${names.join(', ')}`;
  });
}

/* --- Checks and payload ------------------------------------------------ */

export type RowErrors = Partial<
  Record<'regions' | 'days' | 'perOrder' | 'perItem' | 'unit', string>
>;

export type DraftErrors = {
  /** A method with no rows, by name. */
  methods: Record<string, string>;
  rows: Record<string, RowErrors>;
  speeds: Record<string, Partial<Record<'marketplaceId' | 'speed', string>>>;
};

function rowErrors(row: MethodRow): RowErrors {
  const errors: RowErrors = {};
  const states = Object.values(row.regions).flatMap(subRegions =>
    Object.values(subRegions).flatMap(entries => Object.keys(entries)),
  );
  if (states.length === 0) errors.regions = 'Pick at least one region';

  if (!Number(row.minDays) || !Number(row.maxDays)) {
    errors.days = 'Min and max days are required';
  } else if (Number(row.minDays) > Number(row.maxDays)) {
    errors.days = 'Max days must not be less than min days';
  }

  if (!row.free) {
    if (row.perOrder === '') errors.perOrder = 'Required';
    if (row.perItem === '') errors.perItem = 'Required';
    if (!row.unit) errors.unit = 'Required';
  }
  return errors;
}

export function validateDraft(draft: TemplateDraft): DraftErrors {
  const errors: DraftErrors = { methods: {}, rows: {}, speeds: {} };
  for (const method of draft.methods) {
    if (method.rows.length === 0) {
      errors.methods[method.name] = 'Add at least one region row';
    }
    for (const row of method.rows) {
      const found = rowErrors(row);
      if (Object.keys(found).length > 0) errors.rows[row.id] = found;
    }
  }
  for (const speed of draft.speeds) {
    const found: DraftErrors['speeds'][string] = {};
    if (!speed.marketplaceId) found.marketplaceId = 'Required';
    if (!speed.speed) found.speed = 'Required';
    if (Object.keys(found).length > 0) errors.speeds[speed.id] = found;
  }
  return errors;
}

export function hasErrors(errors: DraftErrors) {
  return (
    Object.keys(errors.methods).length > 0 ||
    Object.keys(errors.rows).length > 0 ||
    Object.keys(errors.speeds).length > 0
  );
}

export function toTemplatePayload(
  draft: TemplateDraft,
  tree: RegionTree,
  regionId: string | undefined,
) {
  return {
    marketplace_region_id: regionId,
    default_marketplace_shipping_speed: draft.speeds.map(speed => ({
      ...(speed._id ? { _id: speed._id } : {}),
      marketplace_id: speed.marketplaceId,
      speed: speed.speed,
    })),
    international_shipment: draft.international.map(row => ({
      ...(row._id ? { _id: row._id } : {}),
      marketplace_region: row.regionId,
      price: Number(row.price || 0),
    })),
    shipping_methods: draft.methods.map(method => ({
      method_name: method.name,
      configuration: method.rows.map(row => ({
        ship_charge: {
          per_order: { value: Number(row.perOrder || 0), currency: 'USD' },
          per_item: {
            price: { value: Number(row.perItem || 0), currency: 'USD' },
            unit_of_measure: row.unit || 'item',
          },
        },
        marketplaces: row.marketplaces,
        free: row.free,
        transit_time: {
          min_days: Number(row.minDays),
          max_days: Number(row.maxDays),
        },
        regions: Object.entries(row.regions).map(([code, subRegions]) => ({
          region_code: code,
          region_name:
            tree.find(region => region.regionCode === code)?.regionName ?? code,
          /* The API has always stored the code as the sub-region's name. */
          subRegions: Object.entries(subRegions).map(([subCode, states]) => ({
            subRegionCode: subCode,
            subRegionName: subCode,
            states: Object.keys(states),
          })),
        })),
      })),
    })),
  };
}
